/*!
 * JAWLINE RUN - Fighter landing page behavior.
 * Loads after jawline-run-fighter-art.js, jawline-run-fighter-scenery.js,
 * and jawline-run-fighter.js. Use #play to open the game directly.
 */
(function () {
  'use strict';

  function drawSprite(cv, img) {
    const c = cv.getContext('2d');
    c.clearRect(0, 0, cv.width, cv.height);
    c.imageSmoothingEnabled = false;
    const k = Math.min(cv.width / img.width, cv.height / img.height);
    const w = img.width * k, h = img.height * k;
    c.drawImage(img, (cv.width - w) / 2, cv.height - h, w, h);
  }

  // A pit: two ground edges with a gap, drawn in the game's brick style
  function drawPit(cv, accent) {
    const c = cv.getContext('2d'), s = cv.width / 20;
    c.imageSmoothingEnabled = false;
    c.fillStyle = '#b5643c'; c.fillRect(0, 10 * s, 6 * s, 10 * s); c.fillRect(14 * s, 10 * s, 6 * s, 10 * s);
    c.fillStyle = accent; c.fillRect(0, 10 * s, 6 * s, 2 * s); c.fillRect(14 * s, 10 * s, 6 * s, 2 * s);
    c.fillStyle = '#5a2a14'; c.fillRect(0, 15 * s, 6 * s, s); c.fillRect(14 * s, 15 * s, 6 * s, s); c.fillRect(3 * s, 12 * s, s, 3 * s); c.fillRect(17 * s, 16 * s, s, 4 * s);
  }

  function setup(root) {
    if (root.__jlp) return;
    root.__jlp = true;
    let accent = (root.dataset.accent || '').trim();
    if (!/^#[0-9a-f]{6}$/i.test(accent)) accent = '#c6ff3d';

    fighterArt(root, accent);

    // Game window
    const dlg = root.querySelector('.jlp-modal');
    if (!dlg) return;
    const game = dlg.querySelector('[data-jawline-run-fighter]');
    const hero = root.querySelector('.jlp-hero__scene');
    const heroGame = () => hero && (hero.__jlrFighter);
    const setHero = (on) => { const h = heroGame(); if (h) { h.visible = on; h.updateActive(); } };

    const open = () => {
      if (dlg.open) return;
      if (typeof dlg.showModal === 'function') dlg.showModal(); else dlg.setAttribute('open', '');
      document.documentElement.classList.add('jlp-lock');
      setHero(false); // pause the attract demo behind the window
      if (game) game.focus({ preventScroll: true });
    };
    const close = () => {
      if (typeof dlg.close === 'function') dlg.close(); else { dlg.removeAttribute('open'); onClose(); }
    };
    const board = setupBoard(root, game);
    const onClose = () => {
      document.documentElement.classList.remove('jlp-lock');
      setHero(true);
      board.then((b) => b && b.flush(true)); // a finished run without a name still counts
    };
    dlg.addEventListener('close', onClose);
    dlg.addEventListener('click', (e) => { if (e.target === dlg) close(); }); // click on the backdrop
    root.querySelectorAll('[data-jlp-open]').forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); open(); }));
    const x = dlg.querySelector('.jlp-close');
    if (x) x.addEventListener('click', close);
    if (location.hash === '#play') open();
  }

  // ---------------------------------------------------------------------------
  // Fighter edition art: portraits, animated run cycles, poses, game objects and stage previews
  // ---------------------------------------------------------------------------
  const loaded = (img) => (img.complete && img.naturalWidth ? Promise.resolve() : new Promise((ok) => { img.onload = ok; img.onerror = ok; }));
  function fighterArt(root, accent) {
    const F = window.JawlineRunFighter;
    if (!F) return;
    const ART = F.ART || {};
    root.querySelectorAll('img[data-portrait]').forEach((im) => {
      const src = (ART.portraits || [])[Number(im.dataset.portrait)];
      if (src) im.src = src;
    });
    const art = F.sprites.buildArt(accent);
    const imgs = [].concat(art.pouch, art.bot, [art.ball, art.box]).filter(Boolean);
    Promise.all(imgs.map(loaded)).then(() => {
      root.querySelectorAll('canvas[data-art]').forEach((cv) => {
        const k = cv.dataset.art;
        if (k === 'pit') drawPit(cv, accent);
        else if (k === 'bot') drawSprite(cv, art.bot[0]);
        else if (/^pouch\d$/.test(k)) drawSprite(cv, art.pouch[Number(k.slice(5))] || art.pouch[0]);
        else if (art[k]) drawSprite(cv, art[k]);
      });
      stagePreviews(root, F, accent, art);
    });

    // Fighter sprites need the sprite sheet decoded: retry until the game reports frames
    const anims = [];
    const ready = (tries) => {
      if (!F.sprites.runnerFrames(F.OUTFITS[0])) { if (tries < 200) setTimeout(() => ready(tries + 1), 50); return; }
      root.querySelectorAll('canvas[data-fighter]').forEach((cv) => {
        const fr = F.sprites.runnerFrames(F.OUTFITS[Number(cv.dataset.fighter)] || F.OUTFITS[0]);
        const R = ART.runner;
        cv.width = R.w; cv.height = R.h;
        const pose = cv.dataset.pose || 'run';
        if (pose === 'run') anims.push({ cv, frames: fr.run, i: (Math.random() * fr.run.length) | 0, on: false });
        else drawCropped(cv, pose === 'kick' ? fr.punch || fr.jump : fr.jump);
      });
      if (!anims.length) return;
      anims.forEach((a) => drawFrame(a.cv, a.frames[a.i]));
      if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((es) => es.forEach((e) => { const a = anims.find((q) => q.cv === e.target); if (a) a.on = e.isIntersecting; }));
        anims.forEach((a) => io.observe(a.cv));
      } else anims.forEach((a) => { a.on = true; });
      let last = 0;
      const tick = (t) => {
        if (t - last > 1000 / 18) {   // 18 frames per second, close to the in-game run
          last = t;
          for (const a of anims) if (a.on && !document.hidden) { a.i = (a.i + 1) % a.frames.length; drawFrame(a.cv, a.frames[a.i]); }
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    ready(0);
  }
  // A single pose, cropped to the figure so it fills its slot
  function drawCropped(cv, img) {
    const d = img.getContext('2d').getImageData(0, 0, img.width, img.height).data;
    let x0 = img.width, y0 = img.height, x1 = 0, y1 = 0;
    for (let y = 0; y < img.height; y++) for (let x = 0; x < img.width; x++) {
      if (d[(y * img.width + x) * 4 + 3] > 40) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
    }
    if (x1 < x0) return;
    cv.width = x1 - x0 + 1; cv.height = y1 - y0 + 1;
    const c = cv.getContext('2d');
    c.imageSmoothingEnabled = false;
    c.drawImage(img, x0, y0, cv.width, cv.height, 0, 0, cv.width, cv.height);
  }
  function drawFrame(cv, img) {
    const c = cv.getContext('2d');
    c.clearRect(0, 0, cv.width, cv.height);
    c.imageSmoothingEnabled = false;
    c.drawImage(img, 0, 0);
  }
  // A still of each stage, painted with the game's own scenery, sky and floor colors
  function stagePreviews(root, F, accent, art) {
    const SC = window.JawlineRunFighterScenery, list = root.querySelectorAll('canvas[data-stage]');
    if (!SC || !F.THEMES || !list.length) return;
    const scenery = SC.create({ text: F.drawText, brand: root.dataset.brand || 'JAWLINER', accent, pouches: art.pouch });
    const hexRgb = (h) => { const n = parseInt(h.slice(1), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };
    list.forEach((cv) => {
      const i = Number(cv.dataset.stage) || 0, th = F.THEMES[i], c = cv.getContext('2d');
      const VH = 150, k = cv.height / VH, iw = cv.width / k, gb = VH - 24, cam = Number(cv.dataset.cam) || 0;
      c.setTransform(k, 0, 0, k, 0, 0);
      c.imageSmoothingEnabled = false;
      const g = c.createLinearGradient(0, 0, 0, gb);
      [th.sky0, th.sky1, th.sky2, th.sky3].forEach((col, j) => g.addColorStop(j / 3, col));
      c.fillStyle = g; c.fillRect(0, 0, iw, gb);
      if (th.moon) { c.fillStyle = accent; c.beginPath(); c.arc(iw - 30, 26, 9, 0, Math.PI * 2); c.fill(); c.fillStyle = th.sky1; c.beginPath(); c.arc(iw - 26, 23, 8, 0, Math.PI * 2); c.fill(); }
      if (th.night) { c.fillStyle = '#ffffff'; for (let s = 0; s < 40; s++) c.fillRect((s * 73.3) % iw, (s * 37.7) % (gb - 50), 0.5, 0.5); }
      if (th.sun) { c.fillStyle = th.sunCol; c.globalAlpha = th.sun; c.beginPath(); c.arc(iw * 0.66, gb - 52, 15, 0, Math.PI * 2); c.fill(); c.globalAlpha = 1; }
      scenery.draw(c, i, i, 1, cam, gb, iw, 2.2 + i);
      // floor: slabs or path in the stage's colors
      const ramp = th.floor;
      c.fillStyle = ramp[2]; c.fillRect(0, gb, iw, VH - gb);
      c.fillStyle = ramp[5]; c.fillRect(0, gb, iw, 1);
      c.fillStyle = th.slab ? ramp[0] : ramp[4]; c.fillRect(0, gb + 1, iw, 1);
      if (th.slab) { c.fillStyle = ramp[4]; for (let x = -cam % 22; x < iw; x += 22) for (let y = gb + 2; y < VH; y++) c.fillRect(x - (y - gb) * ((x - iw / 2) / iw) * 0.9, y, 0.5, 1); c.fillRect(0, gb + 9, iw, 0.5); }
      else { const a = hexRgb(ramp[4]); c.fillStyle = `rgb(${a})`; for (let s = 0; s < 260; s++) c.fillRect((s * 47.9) % iw, gb + 2 + ((s * 13.7) % (VH - gb - 2)), 0.5, 0.5); }
    });
  }

  // ---------------------------------------------------------------------------
  // High scores. One record per runner: { name, best (stages 0–4), pouches, seconds, runs }.
  // Storage, first match wins: the data-scores-url backend, the claude.ai preview
  // database, or (on localhost only) this browser.
  // ---------------------------------------------------------------------------
  const pad = (n) => String(n).padStart(2, '0');
  function fmtTime(sec) {
    sec = Math.max(0, Math.round(sec || 0));
    const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
    return h ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
  }
  const fmtNum = (n) => Math.round(n || 0).toLocaleString('en-US');
  const tierOf = (v) => Math.max(0, Math.min(4, Number(v) | 0));
  const toPlayer = (d) => ({
    name: String(d.name || 'RUNNER').slice(0, 12), best: tierOf(d.best), pouches: Number(d.pouches) || 0,
    seconds: Number(d.seconds) || 0, runs: Number(d.runs) || 0, first: Number(d.first) || 0
  });
  // Highest discount first, then most pouches, then whoever got there first
  const rankPlayers = (list) => list.slice().sort((a, b) => (b.best - a.best) || (b.pouches - a.pouches) || (a.first - b.first));
  function summarize(list) {
    const t = { players: list.length, runs: 0, pouches: 0, seconds: 0, tiers: [0, 0, 0, 0, 0] };
    list.forEach((p) => { t.runs += p.runs; t.pouches += p.pouches; t.seconds += p.seconds; t.tiers[p.best]++; });
    return t;
  }
  function mergeRun(prev, run) {
    prev = prev || {};
    const now = Date.now();
    return {
      name: run.name || prev.name || 'RUNNER',
      best: Math.max(tierOf(prev.best), tierOf(run.stages)),
      pouches: (Number(prev.pouches) || 0) + (run.pouches || 0),
      seconds: (Number(prev.seconds) || 0) + (run.seconds || 0),
      runs: (Number(prev.runs) || 0) + (run.runs == null ? 1 : run.runs),
      first: Number(prev.first) || now,
      updated: now
    };
  }
  function localId() {
    try {
      let id = localStorage.getItem('jawlineRunPlayer');
      if (!id) {
        id = window.crypto && crypto.randomUUID ? crypto.randomUUID() : 'p' + Date.now() + Math.random().toString(36).slice(2);
        localStorage.setItem('jawlineRunPlayer', id);
      }
      return id;
    } catch (e) { return 'p' + Date.now(); }
  }

  // Your own backend (see backend/leaderboard-worker.js): GET /leaderboard, POST /runs
  function endpointStore(url) {
    const id = localId();
    return {
      mode: 'live',
      async load() {
        const r = await fetch(`${url}/leaderboard?me=${encodeURIComponent(id)}`, { headers: { accept: 'application/json' } });
        if (!r.ok) throw new Error('load ' + r.status);
        const d = await r.json();
        return { top: (d.top || []).map((p) => Object.assign(toPlayer(p), { you: !!p.you })), totals: d.totals };
      },
      async submit(run) {
        const r = await fetch(`${url}/runs`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(Object.assign({ playerId: id }, run)) });
        if (!r.ok) throw new Error('submit ' + r.status);
      }
    };
  }
  // claude.ai preview: shared database, one document per signed-in viewer (scores/<their id>)
  function dbStore(db, uid) {
    const col = db.collection('scores');
    return {
      mode: uid ? 'preview' : 'preview-readonly', me: uid,
      subscribe(cb, err) {
        return col.onSnapshot((snap) => cb(snap.docs.filter((d) => d.exists).map((d) => Object.assign(toPlayer(d.data()), { id: d.id }))), err);
      },
      async submit(run) {
        if (!uid) throw new Error('signed out');
        const ref = col.doc(uid);
        const snap = await ref.get();
        await ref.set(mergeRun(snap.exists ? snap.data() : null, run));
      }
    };
  }
  // Local preview: this browser only
  function localStore() {
    const key = 'jawlineRunBoard:v1', me = localId();
    const read = () => { try { return JSON.parse(localStorage.getItem(key)) || {}; } catch (e) { return {}; } };
    let listener = null;
    const emit = () => listener && listener(Object.entries(read()).map(([id, d]) => Object.assign(toPlayer(d), { id })));
    return {
      mode: 'local', me,
      subscribe(cb) { listener = cb; emit(); return () => { listener = null; }; },
      async submit(run) {
        const all = read();
        all[me] = mergeRun(all[me], run);
        try { localStorage.setItem(key, JSON.stringify(all)); } catch (e) { /* storage blocked */ }
        emit();
      }
    };
  }

  function renderBoard(board, data, me, pcts) {
    const ranks = board.querySelector('[data-jlp-ranks]');
    ranks.textContent = '';
    for (let i = 0; i < 10; i++) {
      const p = data.top[i];
      const row = document.createElement('div');
      row.setAttribute('role', 'row');
      row.className = 'jlp-rank' + (p ? (i < 3 ? ` jlp-rank--${i + 1}` : '') : ' jlp-rank--empty');
      const cell = (cls, text) => {
        const c = document.createElement('span');
        c.className = cls; c.setAttribute('role', 'cell');
        if (text != null) c.textContent = text;
        row.append(c);
        return c;
      };
      cell('jlp-rank__pos', String(i + 1));
      const name = cell('jlp-rank__name', p ? p.name : '---');
      if (p && (p.you || (me && p.id === me))) {
        const y = document.createElement('span');
        y.className = 'jlp-rank__you'; y.textContent = 'You';
        name.append(y);
      }
      cell('jlp-rank__best', p ? (p.best ? `${pcts[p.best - 1]}%` : '0%') : '--');
      const po = cell('jlp-rank__pouches');
      if (p) po.append(document.createElement('i'), fmtNum(p.pouches)); else po.textContent = '--';
      cell('jlp-rank__time', p ? fmtTime(p.seconds) : '--:--');
      ranks.append(row);
    }
    const t = data.totals || { players: 0, runs: 0, pouches: 0, seconds: 0, tiers: [0, 0, 0, 0, 0] };
    board.querySelector('[data-jlp-total="players"]').textContent = fmtNum(t.players);
    board.querySelector('[data-jlp-total="runs"]').textContent = fmtNum(t.runs);
    board.querySelector('[data-jlp-total="pouches"]').textContent = fmtNum(t.pouches);
    board.querySelector('[data-jlp-total="seconds"]').textContent = fmtTime(t.seconds);
    const tiers = board.querySelector('[data-jlp-tiers]');
    tiers.textContent = '';
    const counts = t.tiers || [0, 0, 0, 0, 0];
    const max = Math.max(1, ...counts);
    [4, 3, 2, 1, 0].forEach((k) => {
      const label = k ? `${pcts[k - 1]}% off` : 'No discount';
      const n = counts[k] || 0;
      const li = document.createElement('li');
      li.className = `jlp-tier jlp-tier--${k}`;
      li.setAttribute('aria-label', `${label}: ${n} ${n === 1 ? 'runner' : 'runners'}`);
      const l = document.createElement('span'); l.className = 'jlp-tier__label'; l.textContent = label;
      const bar = document.createElement('span'); bar.className = 'jlp-tier__bar';
      const fill = document.createElement('i'); fill.style.width = `${(n / max) * 100}%`; bar.append(fill);
      const c = document.createElement('span'); c.className = 'jlp-tier__count'; c.textContent = fmtNum(n);
      li.append(l, bar, c);
      tiers.append(li);
    });
  }

  async function setupBoard(root, game) {
    const board = root.querySelector('[data-jlp-board]');
    if (!board || !('leaderboard' in root.dataset)) return null;
    const pcts = ((game && game.dataset.percents) || '5,10,15,20').split(',').map((n) => parseInt(n, 10) || 0);
    const note = board.querySelector('[data-jlp-board-note]');
    const say = (text) => { note.hidden = !text; note.textContent = text || ''; };

    let store = null;
    const url = (root.dataset.scoresUrl || '').trim().replace(/\/+$/, '');
    if (url) store = endpointStore(url);
    else if (window.claude && typeof window.claude.use === 'function') {
      const [db, user] = await Promise.all([window.claude.use('db'), window.claude.use('user')]);
      if (db) store = dbStore(db, user ? await user.id() : null);
    }
    if (!store && /^(localhost|127\.0\.0\.1|\[::1\])$/.test(location.hostname)) store = localStore();
    if (!store) return null; // nowhere to keep scores: the section stays hidden

    board.hidden = false;
    if (store.mode === 'local') say('Local preview: only runs from this browser are listed.');
    if (store.mode === 'preview-readonly') say('Sign in to claude.ai to add your runs to the list.');
    const show = (list) => renderBoard(board, { top: rankPlayers(list).slice(0, 10), totals: summarize(list) }, store.me, pcts);
    renderBoard(board, { top: [] }, store.me, pcts);

    let refresh = async () => {};
    if (store.subscribe) store.subscribe(show, () => say('The high scores could not be loaded right now.'));
    else {
      refresh = async () => {
        try { const d = await store.load(); renderBoard(board, d, null, pcts); }
        catch (e) { say('The high scores could not be loaded right now.'); }
      };
      refresh();
    }

    // Runs from the game window. A run waits for the player's name; if they skip it,
    // it is saved under a generated name when the window closes or the next run starts.
    let pending = null;
    const autoName = () => {
      try {
        let n = localStorage.getItem('jawlineRunAutoName');
        if (!n) { n = 'RUNNER ' + (100 + Math.floor(Math.random() * 900)); localStorage.setItem('jawlineRunAutoName', n); }
        return n;
      } catch (e) { return 'RUNNER'; }
    };
    const send = async (run) => {
      try { await store.submit(run); await refresh(); }
      catch (e) { if (store.mode !== 'preview-readonly') say('Your last run could not be saved to the high scores.'); }
    };
    const flush = (force) => {
      if (!pending || (!pending.name && !force)) return;
      const run = pending;
      pending = null;
      run.name = run.name || autoName();
      send(run);
    };
    if (game) {
      game.addEventListener('jawlinerun:end', (e) => {
        const d = e.detail || {};
        pending = { stages: d.stages | 0, pouches: d.gums | 0, seconds: d.seconds | 0, name: d.name || '' };
        flush(false);
      });
      game.addEventListener('jawlinerun:name', (e) => {
        const name = e.detail && e.detail.name;
        if (!name) return;
        if (pending) { pending.name = name; flush(false); }
        else send({ name, stages: 0, pouches: 0, seconds: 0, runs: 0 }); // renamed later
      });
      game.addEventListener('jawlinerun:start', () => flush(true));
    }
    return { flush };
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-jawline-landing]').forEach(setup);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
